import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Sparkles, Eye, Trash2, Download, CheckCircle, Clock, FileCheck, Briefcase, Shield, Award, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

const DocumentsGenerator = () => {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [formData, setFormData] = useState({});
  const [generating, setGenerating] = useState(false);

  const categoryIcons = {
    contrat: Briefcase,
    attestation: FileCheck,
    sanction: AlertTriangle,
    formation: Award
  };

  const categoryColors = {
    contrat: 'bg-blue-100 text-blue-800',
    attestation: 'bg-green-100 text-green-800',
    sanction: 'bg-red-100 text-red-800',
    formation: 'bg-purple-100 text-purple-800'
  };

  const categoryLabels = {
    contrat: 'Contrat',
    attestation: 'Attestation',
    sanction: 'Sanction',
    formation: 'Formation'
  };

  const statusColors = {
    brouillon: 'bg-gray-100 text-gray-800',
    finalise: 'bg-blue-100 text-blue-800',
    signe: 'bg-green-100 text-green-800',
    archive: 'bg-gray-100 text-gray-500'
  };

  const statusLabels = {
    brouillon: 'Brouillon',
    finalise: 'Finalisé',
    signe: 'Signé',
    archive: 'Archivé'
  };

  useEffect(() => {
    loadTemplates();
    loadDocuments();
  }, [filterCategory]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await axios.get(`${API_URL}/api/documents/templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await axios.get(`${API_URL}/api/documents/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    const initialData = {};
    template.variables.forEach(variable => {
      initialData[variable] = '';
    });
    setFormData(initialData);
    setShowGenerateModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/documents/generate`,
        {
          templateId: selectedTemplate.id,
          variables: formData,
          useAI: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Document généré avec succès !');
      setShowGenerateModal(false);
      loadDocuments();
      setSelectedDocument(response.data);
    } catch (error) {
      console.error('Erreur génération:', error);
      alert('Erreur lors de la génération du document');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Sparkles className="h-8 w-8 text-purple-600" />
                Générateur de Documents IA
              </h1>
              <p className="text-gray-600 mt-1">Créez vos documents juridiques en quelques clics avec IA open source (Mistral)</p>
            </div>
          </div>

          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Rechercher un template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Toutes les catégories</option>
              <option value="contrat">Contrats</option>
              <option value="attestation">Attestations</option>
              <option value="sanction">Sanctions</option>
              <option value="formation">Formations</option>
            </select>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow-lg p-6 mb-6 text-white">
          <div className="flex items-start gap-4">
            <Sparkles className="h-12 w-12 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold mb-2">✨ Générateur Intelligent avec IA open source (Mistral)</h3>
              <p className="text-purple-100">
                Documents juridiques professionnels conformes au droit français et à la convention collective 1351 de la sécurité privée.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📄 Templates Disponibles ({filteredTemplates.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => {
              const Icon = categoryIcons[template.category];
              return (
                <div
                  key={template.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-200 cursor-pointer"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <Icon className="h-6 w-6 text-purple-600" />
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${categoryColors[template.category]}`}>
                      {categoryLabels[template.category]}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>
                  
                  {template.poste && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      <Shield className="h-4 w-4" />
                      <span className="capitalize">{template.poste.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  
                  <button className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Générer
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">📋 Documents Générés</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-500">Chargement...</div>
          ) : documents.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucun document généré</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                      <div className="text-xs text-gray-500">{doc.templateName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${categoryColors[doc.category]}`}>
                        {categoryLabels[doc.category]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{doc.agentName || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors[doc.status]}`}>
                        {statusLabels[doc.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setSelectedDocument(doc)} className="text-indigo-600 hover:text-indigo-900">
                          <Eye className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleDelete(doc.id)} className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showGenerateModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-purple-600" />
                {selectedTemplate.name}
              </h2>
              <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>

            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded mb-6">
                <p className="text-sm text-purple-900">
                  ✨ <strong>IA open source activée</strong> - Document conforme au droit français
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto p-2">
                {selectedTemplate.variables.map((variable) => (
                  <div key={variable}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                      {variable.replace(/([A-Z])/g, ' $1').trim()} *
                    </label>
                    <input
                      type={variable.toLowerCase().includes('date') ? 'date' : 'text'}
                      name={variable}
                      value={formData[variable] || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-6 border-t">
                <button type="button" onClick={() => setShowGenerateModal(false)} className="flex-1 bg-gray-500 text-white px-4 py-3 rounded-lg" disabled={generating}>
                  Annuler
                </button>
                <button type="submit" className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2" disabled={generating}>
                  {generating ? <><Clock className="h-5 w-5 animate-spin" />Génération...</> : <><Sparkles className="h-5 w-5" />Générer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedDocument && !showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold">{selectedDocument.title}</h2>
                <span className={`mt-2 inline-flex px-3 py-1 text-xs font-semibold rounded-full ${statusColors[selectedDocument.status]}`}>
                  {statusLabels[selectedDocument.status]}
                </span>
              </div>
              <button onClick={() => setSelectedDocument(null)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border">
              <ReactMarkdown>{selectedDocument.content}</ReactMarkdown>
            </div>

            <div className="flex gap-3 mt-6">
              <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2">
                <Download className="h-5 w-5" />PDF
              </button>
              <button className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5" />Finaliser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsGenerator;
